import { useQuery } from '@apollo/client';
import { Delta } from 'quill/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import calculateVersionChanges from './calculateVersionChanges';
import NewVersionChangesWall from './NewVersionChangesWall';
import VersionChanges from './VersionChanges';
import VersionChangesDropdown from './VersionChangesDropdown';
import Page from '../../app/layout/Page';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import AttributeHandler from '../../editor/AttributeHandler';
import type {
  MagneticWallFocus,
  MagneticWallHighlight,
} from '../../editor/editorSlice';
import QuillContext from '../../editor/QuillContext';
import useSyncMagneticWallStates from '../../editor/useSyncMagneticWallState';
import { gql } from '../../graphql/generated';
import InternalTopbar from '../../topbar/InternalTopbar';
import PageMeta from '../../utils/PageMeta';
import useNavigate from '../../utils/router/useNavigate';
import postPath from '../../utils/url/postPath';
import { scrollMagneticWallItemIntoView } from '../MagneticWallItem';
import PostLayout from '../PostLayout';
import PostLayoutProvider from '../PostLayoutProvider';
import parseMagneticWallHash from '../utils/parseMagneticWallHash';

const NEW_POST_HISTORY_VIEW_QUERY = gql(/** @complexity 22 */ `
    query NewPostHistoryView($id: ID!, $versionFrom: Int!, $versionTo: Int!) {
      post(id: $id) {
        id
        title
        histories {
          id
          versionFrom
          versionTo
        }
        history(versionFrom: $versionFrom, versionTo: $versionTo) {
          id
          snapshot
          diff
          insertedAt
          ...NewVersionChangesWall_history
        }
        ...VersionChangesDropdown_post
        ...NewVersionChangesWall_post
      }
      entityAssetToken(entityId: $id, entityType: POST) {
        ...AssetTokenProvider_entityAssetToken
      }
    }
  `);

interface NewPostHistoryViewProps {
  postId: string;
  from: string;
  to: string;
}

const InnerNewPostHistoryView = ({
  postId,
  from,
  to,
}: NewPostHistoryViewProps) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const versionFrom = parseInt(from, 10);
  const versionTo = parseInt(to, 10);

  const { data } = useQuery(NEW_POST_HISTORY_VIEW_QUERY, {
    variables: {
      id: postId,
      versionFrom,
      versionTo,
    },
  });

  const post = data?.post;

  const sortedHistories = (post?.histories ?? []).toSorted(
    (a, b) => a.versionTo - b.versionTo,
  );

  const minHistory = sortedHistories
    .toReversed()
    .find((history) => versionFrom >= history.versionFrom);

  const maxHistory = sortedHistories.find(
    (history) => versionTo <= history.versionTo,
  );

  useEffect(() => {
    if (maxHistory) {
      setSearchParams({
        histories: `${minHistory?.versionFrom != null ? minHistory.versionFrom : maxHistory.versionFrom}-${maxHistory.versionTo}`,
      });
    }
  }, [setSearchParams, minHistory?.versionFrom, maxHistory]);

  // We have this in a separate useMemo to avoid recalculating the version changes
  // when history changes but the diff object is the same. This is for example
  // the case when the user unresolves a thread.
  const calculated = useMemo(
    () =>
      post?.history.diff ? calculateVersionChanges(post.history.diff) : null,
    [post?.history.diff],
  );

  const { content, allVersionChanges } = useMemo(() => {
    if (!post?.history.diff || !calculated) {
      return { content: undefined, allVersionChanges: undefined };
    }

    const composed = new Delta(post?.history.snapshot)
      .compose(new Delta(post?.history.diff))
      .compose(calculated.delta ?? new Delta());

    return {
      content: composed,
      allVersionChanges: calculated.versionChanges,
    };
  }, [calculated, post?.history.snapshot, post?.history.diff]);

  const [magneticWallHighlight, setMagneticWallHighlight] =
    useState<MagneticWallHighlight | null>(null);
  const [magneticWallFocus, setMagneticWallFocus] =
    useState<MagneticWallFocus | null>(null);

  useSyncMagneticWallStates({ magneticWallFocus, magneticWallHighlight });

  const { hash } = useLocation();
  useEffect(() => {
    const parseResult = parseMagneticWallHash(hash);
    if (!parseResult) return;

    setMagneticWallFocus({
      active: true,
      id: parseResult.id,
      type: parseResult.type,
      hasExpandIntent: false,
    });

    scrollMagneticWallItemIntoView(parseResult.id);
  }, [hash]);

  if (!post) {
    return null;
  }

  const postTitle = post.title;
  const initialTitle = 'History - Slab';

  const getHistoryForVersion = (version: number) =>
    post.histories.find(
      (h) => h.versionFrom < version && h.versionTo >= version,
    );

  const lastHistory = sortedHistories[sortedHistories.length - 1];
  const historyInsertedAt =
    maxHistory == null && lastHistory
      ? new Date(lastHistory.insertedAt)
      : new Date(post.history.insertedAt);

  return (
    <VersionChanges.Root
      allVersionChanges={allVersionChanges}
      onFocusChange={() => setMagneticWallFocus(null)}
      hideFocusedDiff={magneticWallFocus != null}
    >
      <PageMeta
        title={postTitle ? `${postTitle} - ${initialTitle}` : initialTitle}
      />
      <PostLayoutProvider hasMagneticWallItems isTOCDisabled>
        <PostLayout.Root
          topbar={
            <VersionChanges.PostLayoutTopbarContainer>
              <VersionChanges.Topbar>
                <VersionChangesDropdown.Root>
                  <VersionChangesDropdown.TriggerButton>
                    {maxHistory ? `Version ${maxHistory.versionTo}` : 'Latest'}
                  </VersionChangesDropdown.TriggerButton>
                  <VersionChangesDropdown.Content post={post} />
                </VersionChangesDropdown.Root>
              </VersionChanges.Topbar>
            </VersionChanges.PostLayoutTopbarContainer>
          }
        >
          <PostLayout.Above />
          <PostLayout.TOC />
          <PostLayout.Editor>
            <AttributeHandler
              onAttributeClick={(attribute, type) => {
                setMagneticWallFocus({
                  active: true,
                  id: attribute.id,
                  type,
                  hasExpandIntent: false,
                });
              }}
            >
              <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
                <VersionChanges.Post
                  key={`${versionFrom}-${versionTo}`}
                  content={content}
                />
              </AssetTokenProvider>
            </AttributeHandler>
          </PostLayout.Editor>
          <PostLayout.MagneticWall>
            <NewVersionChangesWall
              post={post}
              history={post.history}
              historyInsertedAt={historyInsertedAt}
              isAtCurrentHistory={(version) => {
                const history = getHistoryForVersion(version);
                return (
                  history?.versionFrom === versionFrom &&
                  history?.versionTo === versionTo
                );
              }}
              goToVersion={(version) => {
                const history = getHistoryForVersion(version);
                if (history) {
                  navigate(
                    postPath(post, {
                      histories: `${history.versionFrom}-${history.versionTo}`,
                    }),
                  );
                }
              }}
              setMagneticWallFocus={setMagneticWallFocus}
              magneticWallFocus={magneticWallFocus}
              setMagneticWallHighlight={setMagneticWallHighlight}
              magneticWallHighlight={magneticWallHighlight}
              versionFrom={versionFrom}
              versionTo={versionTo}
            />
          </PostLayout.MagneticWall>
        </PostLayout.Root>
      </PostLayoutProvider>
    </VersionChanges.Root>
  );
};

const NewPostHistoryView = (props: NewPostHistoryViewProps) => {
  return (
    <Page topbar={<InternalTopbar />}>
      <QuillContext>
        <InnerNewPostHistoryView {...props} />
      </QuillContext>
    </Page>
  );
};

export default NewPostHistoryView;
